import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/layouts/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import UserFilters from './components/UserFilters';
import UserTable from './components/UserTable';
import AddEditUserModal from './components/AddEditUserModal';
import { userService } from '../../services/userService';
import ActionToast from '../../components/ui/ActionToast';

const userListCache = new Map();
const getUserListCacheKey = (isStaffMode, filters) => `${isStaffMode ? 'staff' : 'users'}:${JSON.stringify(filters)}`;

const EnhancedUserManagementSystem = ({ isStaffMode: propIsStaffMode = false }) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { profile } = useAuth();
    const isSystemAdministrator = String(profile?.primary_role || '').toLowerCase() === 'system_administrator';

    const isStaffMode = propIsStaffMode || 
        location.pathname.includes('/staff-management') || 
        searchParams.get('mode') === 'staff' || 
        searchParams.get('role') === 'staff';

    const defaultFilters = {
        role: isStaffMode ? 'all_staff' : 'all',
        status: 'all',
        search: ''
    };
    const [filters, setFilters] = useState(defaultFilters);
    const initialCacheKey = getUserListCacheKey(isStaffMode, defaultFilters);
    const [users, setUsers] = useState(() => userListCache.get(initialCacheKey) || []);
    const [loading, setLoading] = useState(() => !userListCache.has(initialCacheKey));

    useEffect(() => {
        if (isStaffMode && (filters.role === 'all' || !filters.role)) {
            setFilters(prev => ({ ...prev, role: 'all_staff' }));
        }
    }, [isStaffMode]);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [notice, setNotice] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);
    const [actionInProgress, setActionInProgress] = useState(false);

    // Fetch users on mount and when filters change
    const fetchUsers = async () => {
        const cacheKey = getUserListCacheKey(isStaffMode, filters);
        const cachedUsers = userListCache.get(cacheKey);
        if (cachedUsers) {
            setUsers(cachedUsers);
            setLoading(false);
        }
        // Keep the current list visible while a filter tab or search query is
        // loading. Only show the full-page loader when there is no data yet.
        if (!cachedUsers && users.length === 0) setLoading(true);
        try {
            const data = await userService.getUsers(filters);
            userListCache.set(cacheKey, data || []);
            setUsers(data || []);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchUsers();
        }, 300);
        return () => clearTimeout(debounce);
    }, [filters]);

    // Handlers
    const handleAddUser = () => {
        setCurrentUser(null);
        setIsModalOpen(true);
    };

    const handleEditUser = (user) => {
        setCurrentUser(user);
        setIsModalOpen(true);
    };

    const handleDeleteUser = (userId) => {
        const user = users.find(candidate => candidate.id === userId) || { id: userId };
        setPendingAction({ type: 'delete', user });
    };

    const handleStatusChange = async (userId, newStatus) => {
        const { success, error } = await userService.updateUserStatus(userId, newStatus);
        if (success) {
            userListCache.clear();
            setUsers(currentUsers => {
                const nextUsers = currentUsers.map(u => u.id === userId ? { ...u, status: newStatus } : u);
                userListCache.set(getUserListCacheKey(isStaffMode, filters), nextUsers);
                return nextUsers;
            });
        } else {
            setNotice({ type: 'error', message: error?.message || 'Failed to update status.' });
        }
    };

    const handleResetPassword = async (user) => {
        if (!isSystemAdministrator) {
            setNotice({ type: 'error', message: 'Only system administrators can reset passwords.' });
            return;
        }

        setPendingAction({ type: 'reset-password', user });
    };

    const confirmPendingAction = async () => {
        if (!pendingAction?.user?.id || actionInProgress) return;

        const { type, user } = pendingAction;
        const name = user.full_name || user.name || user.email || 'this user';
        setActionInProgress(true);

        try {
            if (type === 'delete') {
                const { success, error } = await userService.deleteUser(user.id);
                if (!success) throw error || new Error('Failed to delete user.');
                userListCache.clear();
                setNotice({ type: 'success', message: `${name} was deleted successfully.` });
                fetchUsers();
            } else {
                const result = await userService.resetPasswordToIdentity(user.id);
                if (!result.success) throw result.error || new Error('Failed to reset password.');
                setNotice({
                    type: 'success',
                    message: `Password reset for ${name}. Temporary password: ${result.temporary_password}. The user must change it on next login.`
                });
            }
        } catch (error) {
            setNotice({ type: 'error', message: error?.message || (type === 'delete' ? 'Failed to delete user.' : 'Failed to reset password.') });
        } finally {
            setActionInProgress(false);
            setPendingAction(null);
        }
    };

    const handleSaveUser = async (userData) => {
        if (currentUser) {
            const { success, error } = await userService.updateUserProfile(currentUser.id, userData);
            if (!success) throw error;
            setNotice({ type: 'success', message: `${userData.full_name} was updated successfully.` });
        } else {
            const result = await userService.createUser(userData);
            if (!result.success) throw new Error(result.error || 'Failed to create staff account');
            setNotice({ type: 'success', message: `${userData.full_name} was created. Login: ${result.institutional_email} or ${result.pf_number}; initial password: ${result.initial_password || result.pf_number}.` });
        }
        userListCache.clear();
        fetchUsers();
    };

    return (
        <AdminLayout title={isStaffMode ? "Staff Management" : "User Management"}>
            <ActionToast notice={notice} onClose={() => setNotice(null)} />
            {pendingAction && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/40 p-4" role="dialog" aria-modal="true" aria-labelledby="user-action-title">
                    <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl">
                        <h2 id="user-action-title" className="text-lg font-semibold text-foreground">
                            {pendingAction.type === 'delete' ? 'Delete user account?' : 'Reset temporary password?'}
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            {pendingAction.type === 'delete'
                                ? `This permanently removes ${pendingAction.user.full_name || pendingAction.user.email || 'this user'} and cannot be undone.`
                                : `Reset ${pendingAction.user.full_name || pendingAction.user.email || 'this user'}'s password to their identity number? They will be required to change it on next login.`}
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button type="button" onClick={() => setPendingAction(null)} disabled={actionInProgress} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50">
                                Cancel
                            </button>
                            <button type="button" onClick={confirmPendingAction} disabled={actionInProgress} className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${pendingAction.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary/90'}`}>
                                {actionInProgress ? 'Working…' : pendingAction.type === 'delete' ? 'Delete user' : 'Reset password'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="space-y-4">
                <UserFilters
                    filters={filters}
                    onFilterChange={setFilters}
                    onAddUser={handleAddUser}
                    userCount={users.length}
                    isStaffMode={isStaffMode}
                />

                <UserTable
                    users={users}
                    loading={loading}
                    onEdit={handleEditUser}
                    onDelete={handleDeleteUser}
                    onStatusChange={handleStatusChange}
                    onResetPassword={handleResetPassword}
                    canResetPasswords={isSystemAdministrator}
                />

                <AddEditUserModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    user={currentUser}
                    onSave={handleSaveUser}
                />
            </div>
        </AdminLayout>
    );
};

export default EnhancedUserManagementSystem;
